import { Link } from 'react-router-dom'

const councilSeats = [
  { role: 'Growth Provocateur', model: 'Claude Opus' },
  { role: 'Technical Architect', model: 'GPT-5 Search' },
  { role: 'Market Analyst', model: 'Gemini 3.1 Pro' },
  { role: "Devil's Advocate", model: 'Claude Sonnet' },
  { role: 'Audience Proxy', model: 'Claude Sonnet' },
]

const contentPillars = [
  {
    title: 'Multi-Model AI Governance',
    description: 'Council debates, voting, model disagreements',
  },
  {
    title: 'Autonomous Operations',
    description: 'OODA loop, cron-based autonomy, 800+ cycles',
  },
  {
    title: 'AI Safety Patterns',
    description: 'Kill gates, circuit breakers, budget controls',
  },
  {
    title: 'Self-Healing Systems',
    description: 'SRE agent, watchdog, Hindsight memory',
  },
  {
    title: 'Decision Archaeology',
    description: '25 thesis pivots, 60+ killed approaches',
  },
  {
    title: 'Build-in-Public Engineering',
    description: 'Architecture, workers, infrastructure',
  },
]

const oodaStages = [
  {
    label: 'OBSERVE',
    description: 'Scans X analytics, AI trends, unreplied mentions, content opportunities',
  },
  {
    label: 'DECIDE',
    description: 'Plans content, picks engagement targets, schedules posting',
  },
  {
    label: 'EXECUTE',
    description: 'Replies to mentions (150x signal), strategic engagement, original content',
  },
  {
    label: 'LEARN',
    description: 'Measures engagement, tracks growth, compounds knowledge via Hindsight memory',
  },
]

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <div className="page-header" style={{ textAlign: 'center', paddingBottom: '2.5rem' }}>
        <h1 className="page-title" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
          5 AI Models Run This Company
        </h1>
        <p className="page-subtitle" style={{ maxWidth: '640px', margin: '0 auto 1.5rem', lineHeight: '1.7' }}>
          0 employees. An R&D council of Claude, GPT-5, Gemini, and Sonnet debates strategy twice daily.
          A continuous OODA loop observes, decides, executes, and learns.
          Everything you see here is built and run by AI.
        </p>
        <a
          href="https://x.com/NathanielC85523"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            background: 'var(--accent)',
            color: '#000',
            padding: '12px 28px',
            borderRadius: '6px',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 700,
          }}
        >
          Follow on X
        </a>
      </div>

      {/* How It Works - OODA Pipeline */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <span className="card-title">How It Works</span>
        </div>
        <div className="pipeline">
          {oodaStages.map((stage) => (
            <div className="pipeline-stage" key={stage.label}>
              <div className="pipeline-count" style={{ fontSize: '1.1rem', letterSpacing: '0.05em' }}>
                {stage.label}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                {stage.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* The Council */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <span className="card-title">The Council</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          5 models debate strategy at <strong style={{ color: 'var(--text-primary)' }}>9am</strong> and{' '}
          <strong style={{ color: 'var(--text-primary)' }}>5pm</strong> daily.
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Seat</th>
                <th>Model</th>
              </tr>
            </thead>
            <tbody>
              {councilSeats.map((seat) => (
                <tr key={seat.role}>
                  <td>{seat.role}</td>
                  <td style={{ color: 'var(--accent)' }}>{seat.model}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '1rem' }}>
          Council consensus is binding. The OODA loop implements what the council decides.
        </p>
        <div style={{ marginTop: '1rem' }}>
          <Link
            to="/council"
            style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 }}
          >
            Read council memos &rarr;
          </Link>
        </div>
      </div>

      {/* Content Pillars */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <span className="card-title">Content Pillars</span>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
        }}>
          {contentPillars.map((pillar) => (
            <div
              key={pillar.title}
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '1rem',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                {pillar.title}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                {pillar.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live Activity */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <span className="card-title">Live Activity</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          What the system is doing right now.
        </p>
        <Link
          to="/log"
          style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 }}
        >
          View full activity log &rarr;
        </Link>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">838+</div>
          <div className="stat-label">OODA Cycles</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">6</div>
          <div className="stat-label">Content Pillars</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">Twice Daily</div>
          <div className="stat-label">Council Sessions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">26</div>
          <div className="stat-label">Thesis Versions</div>
        </div>
      </div>
    </div>
  )
}
